import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaFacebookF, FaYoutube, FaInstagram, FaTiktok, FaChevronUp } from 'react-icons/fa';
import LOGA from '../../assets/logos/LOG.png';
import Az from '../../assets/logos/azb.png';

// Palette white / beige / gold
const colors = {
  bg: '#FFFFFF',
  gold: '#C69C6D',
  goldDark: '#B88A56',
  goldLight: '#F5E1C8',
  text: '#3B2F2A',
  textSoft: '#5C4A3D',
  cardBg: 'rgba(255, 255, 255, 0.7)',
  cardBorder: 'rgba(198, 156, 109, 0.25)',
  shadow: '0 8px 24px rgba(107, 90, 70, 0.06)',
  glassBlur: 'blur(16px)',
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <style>{`
        .footer-white {
          background: ${colors.bg};
          position: relative;
          overflow: hidden;
          padding: 0;
          font-family: 'Inter', sans-serif;
        }

        /* Légère texture dorée */
        .footer-white::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 20% 30%, ${colors.goldLight}40, transparent 50%),
                      radial-gradient(circle at 80% 70%, ${colors.goldLight}30, transparent 50%);
          z-index: 0;
        }

        .footer-white .container {
          position: relative;
          z-index: 1;
        }

        .footer-main {
          padding: 80px 0 50px;
        }

        .widget-title {
          font-size: 1.3rem;
          font-weight: 700;
          color: ${colors.text};
          margin-bottom: 1.5rem;
          position: relative;
          padding-bottom: 10px;
        }

        .widget-title::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 40px;
          height: 3px;
          background: linear-gradient(90deg, ${colors.gold}, ${colors.goldDark});
          border-radius: 2px;
        }

        /* Cartes glassmorphism */
        .footer-card {
          background: ${colors.cardBg};
          backdrop-filter: ${colors.glassBlur};
          -webkit-backdrop-filter: ${colors.glassBlur};
          border: 1px solid ${colors.cardBorder};
          border-radius: 24px;
          padding: 30px;
          box-shadow: ${colors.shadow};
          height: 100%;
          transition: all 0.3s ease;
        }

        .footer-card:hover {
          background: rgba(255, 255, 255, 0.85);
          box-shadow: 0 12px 28px rgba(184, 138, 86, 0.12);
          border-color: ${colors.gold};
          transform: translateY(-2px);
        }

        .footer-about img {
          width: 220px;
          margin-bottom: 20px;
          filter: drop-shadow(0 4px 8px rgba(0,0,0,0.05));
        }

        .footer-about p {
          color: ${colors.textSoft};
          font-size: 0.95rem;
          line-height: 1.6;
          margin-bottom: 25px;
        }

        /* Icônes sociales */
        .social-icons {
          display: flex;
          gap: 12px;
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .social-icons li a {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: ${colors.cardBg};
          backdrop-filter: ${colors.glassBlur};
          -webkit-backdrop-filter: ${colors.glassBlur};
          border: 1px solid ${colors.cardBorder};
          color: ${colors.gold};
          font-size: 1.1rem;
          transition: all 0.3s ease;
        }

        .social-icons li a:hover {
          background: ${colors.gold};
          color: #fff;
          border-color: ${colors.gold};
          transform: translateY(-3px);
          box-shadow: 0 8px 16px rgba(198, 156, 109, 0.3);
        }

        /* Heures */
        .working-hours {
          color: ${colors.textSoft};
          font-size: 0.95rem;
          line-height: 1.7;
        }

        .working-hours strong {
          color: ${colors.text};
          display: block;
          margin-bottom: 8px;
        }

        /* Liste services */
        .list-arrow {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .list-arrow li {
          margin-bottom: 12px;
        }

        .list-arrow li a {
          color: ${colors.textSoft};
          text-decoration: none;
          font-size: 0.95rem;
          display: flex;
          align-items: center;
          transition: all 0.2s;
        }

        .list-arrow li a::before {
          content: '›';
          font-size: 1.2rem;
          margin-right: 10px;
          color: ${colors.gold};
          font-weight: 700;
          transition: transform 0.2s;
        }

        .list-arrow li a:hover {
          color: ${colors.gold};
          padding-left: 5px;
        }

        .list-arrow li a:hover::before {
          transform: translateX(3px);
        }

        /* Copyright */
        .copyright-section {
          background: ${colors.goldLight}30;
          backdrop-filter: ${colors.glassBlur};
          -webkit-backdrop-filter: ${colors.glassBlur};
          border-top: 1px solid ${colors.cardBorder};
          padding: 30px 0;
          text-align: center;
        }

        .copyright-logo {
          width: 180px;
          margin-bottom: 10px;
          filter: drop-shadow(0 4px 6px rgba(0,0,0,0.05));
        }

        .copyright-text {
          color: ${colors.textSoft};
          font-size: 0.9rem;
        }

        /* Bouton retour en haut */
        .back-to-top {
          position: fixed;
          bottom: 30px;
          right: 30px;
          z-index: 999;
        }

        .back-to-top-btn {
          width: 48px;
          height: 48px;
          border-radius: 16px;
          background: ${colors.cardBg};
          backdrop-filter: ${colors.glassBlur};
          -webkit-backdrop-filter: ${colors.glassBlur};
          border: 1px solid ${colors.cardBorder};
          color: ${colors.gold};
          font-size: 1.2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: ${colors.shadow};
        }

        .back-to-top-btn:hover {
          background: ${colors.gold};
          color: #fff;
          border-color: ${colors.gold};
          transform: translateY(-3px);
          box-shadow: 0 12px 24px rgba(198, 156, 109, 0.3);
        }

        @media (max-width: 768px) {
          .footer-main {
            padding: 60px 0 30px;
          }
          .footer-card {
            margin-bottom: 20px;
          }
        }
      `}</style>

      <footer className="footer-white">
        <div className="footer-main">
          <div className="container">
            <motion.div
              className="row g-4"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {/* Qui Sommes Nous */}
              <div className="col-lg-4 col-md-6">
                <motion.div className="footer-card footer-about" variants={itemVariants}>
                  <h3 className="widget-title">Qui Sommes Nous</h3>
                  <img loading="lazy" src={LOGA} alt="Azyekos Logo" />
                  <p>
                    Azyekos Services est une Conciergerie de Luxe qui vous fournit des services et conseils sur mesure pour tous vos moments privés et professionnels.
                  </p>
                  <ul className="social-icons">
                    <li>
                      <a href="https://web.facebook.com/azyekos" title="Facebook" target="_blank" rel="noreferrer">
                        <FaFacebookF />
                      </a>
                    </li>
                    <li>
                      <a href="https://www.youtube.com/@azyekos-Services" title="YouTube" target="_blank" rel="noreferrer">
                        <FaYoutube />
                      </a>
                    </li>
                    <li>
                      <a href="https://www.instagram.com/azyekos_services_/" title="Instagram" target="_blank" rel="noreferrer">
                        <FaInstagram />
                      </a>
                    </li>
                    <li>
                      <a href="https://www.tiktok.com/@azyekos.lounges" title="TikTok" target="_blank" rel="noreferrer">
                        <FaTiktok />
                      </a>
                    </li>
                  </ul>
                </motion.div>
              </div>

              {/* Heures */}
              <div className="col-lg-4 col-md-6">
                <motion.div className="footer-card" variants={itemVariants}>
                  <h3 className="widget-title">Heures</h3>
                  <div className="working-hours">
                    <strong>À votre service 7j/7</strong>
                    Notre équipe vous accueille et vous accompagne de manière personnalisée de <strong>6h00 à 23h00</strong>, y compris les week-ends et jours fériés, pour vous garantir une assistance continue.
                  </div>
                </motion.div>
              </div>

              {/* Services */}
              <div className="col-lg-4 col-md-6">
                <motion.div className="footer-card" variants={itemVariants}>
                  <h3 className="widget-title">Services</h3>
                  <ul className="list-arrow">
                    <li><Link to="/LifeStyleEvasion">Lifestyle / Évasion</Link></li>
                    <li><Link to="/Bien-etre">Bien-Être</Link></li>
                    <li><Link to="/Conciergerie-privee">Conciergerie Privée</Link></li>
                    <li><Link to="/Culture-tourisme">Culture / Tourisme</Link></li>
                    <li><Link to="/Formalites-aeroportuaire">Formalités Aéroportuaires</Link></li>
                    <li><Link to="/Lounges">Airport Lounge</Link></li>
                    <li><Link to="/RentCars">Location des véhicules</Link></li>
                  </ul>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Copyright */}
        <div className="copyright-section">
          <div className="container">
            <img loading="lazy" src={Az} alt="Azyekos" className="copyright-logo" />
            <p className="copyright-text">
              &copy; {new Date().getFullYear()} Azyekos Services. Tous droits réservés.
            </p>
          </div>
        </div>

        {/* Bouton retour en haut */}
        <div className="back-to-top">
          <button className="back-to-top-btn" onClick={scrollToTop} aria-label="Retour en haut">
            <FaChevronUp />
          </button>
        </div>
      </footer>
    </>
  );
};

export default Footer;